// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';

import {
  Checkbox,
  MultiSelect,
  TextArea,
  TextField,
  Box,
  Token,
  Icon,
  IconName,
  Tooltip,
} from '@usealloy/component-library';
import {
  Controller, useFormContext,
} from 'react-hook-form';

import capitalize from 'lodash/capitalize';
import {
  StyledAttributeFormContainer,
  StyledLeftColumnContainer,
  StyledRightColumnContainer,
} from '../styles';
import {
  StyledCheckbox, StyledTextField, StyledTextAreaWrapper, getDiffStyleFunction,
  PLACEHOLDER_DIFF_STATUS,
} from '../../diffStyles';
import type { FormState } from './types';
import { getDataTypeOptions } from './helpers';
import AcceptedValuesField from '~/dashboard/views/settings/published-attributes/components/AcceptedValuesField';
import ValidValuesField from '~/dashboard/views/settings/published-attributes/components/ValidValuesField';
import { UserContext } from '~/dashboard/context';
import {
  PII_CHECKBOX_LABEL,
  PII_CHECKBOX_HELP_TEXT,
} from '~/dashboard/views/settings/published-attributes/constants';
import { PublishedAttributeOwnerType } from '~/dashboard/views/settings/published-attributes/types';
import DefaultValueField from '../DefaultValueField';
import { getAgentUpdateValueTooltipText } from '~/dashboard/views/settings/published-attributes/components/AddPublishedAttributeModal';
import { usePublishedAttributeGroupingsList } from '~/dashboard/views/settings/published-attributes/hooks';
import type { Option } from '~/journey/DeveloperGuide/types';
import useNodeFormAutoSave from '../../useNodeFormAutoSave';
import type { NodeEditorAutoSaveTabProps } from '../../useNodeEditorForm/types';
import usePreventEmptyRegister from '../../usePreventEmptyRegister';
import { useAppSelector } from '~/store/reduxHooks';
import {
  selectActions, selectDiffMode, selectDirectDependentActions, selectFocusedAction,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import ConfirmDataTypeChangeModal from '../Footer/FooterModals/ConfirmDataTypeChangeModal';
import { publishedAttributeFormFields } from '../types';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';
import { getExistingNamesForActionType, getPublishedDetails, isActionDataObjectEphemeral } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';
import { isAttributeNameUnique, validationMessages } from '../validation';


export default function InputAttributeConfigurationTab({
  handleAutoSave, isReadOnly = false,
}: NodeEditorAutoSaveTabProps) {
  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode) && !isReadOnly;

  const dataTypeOptions = React.useMemo(() => getDataTypeOptions(), []);
  const { permissions } = useContext(UserContext);
  const hasUpdatePublishedAttributesPermission = permissions['UPDATE:PublishedAttributes:null'];

  const focusedAction = useAppSelector(selectFocusedAction);
  const workflowProps = useAppSelector(selectWorkflowProps);
  const actions = useAppSelector(selectActions);
  const isInDiffMode = useAppSelector(selectDiffMode);

  // TODO: hardcoded until the per-field diff status is wired in. The diff-mode guard is
  // redundant once that lookup returns null outside diff mode, and can be dropped then.
  const diffStatus = isInDiffMode ? PLACEHOLDER_DIFF_STATUS : null;

  const existingInputNames = useMemo(
    () => getExistingNamesForActionType(actions, ActionType.INPUT, focusedAction.publicId),
    [actions, focusedAction.publicId],
  );

  const focusedActionDependents = useAppSelector(selectDirectDependentActions);
  const hasDependents = focusedActionDependents.length > 0;

  const {
    register,
    control,
    watch,
    formState,
    setValue,
    handleSubmit,
    getFieldState,
  } = useFormContext<FormState>();

  const preventEmptyRegister = usePreventEmptyRegister<FormState>();

  const {
    data: attributeGroupings,
    isLoading: attributeGroupingsLoading,
  } = usePublishedAttributeGroupingsList();

  const groupingOptions = useMemo(() => (
    (attributeGroupings || [])
      .filter(attribute => !attribute.archived)
      .map(({ groupingName, groupingToken }) => ({
        label: groupingName,
        value: groupingToken,
      }))
  ), [attributeGroupings]);

  const dataType = watch('dataType');
  const usingJq = watch('hasJqFilter');
  const jqFilter = watch('jqFilter');
  const hasExpiration = watch('hasExpiration');
  const publishedAttributeToken = watch('attributeToken');
  const isPublishedAttribute = Boolean(publishedAttributeToken);
  const isSystemPublishedAttribute = isPublishedAttribute
    && (workflowProps?.publishedAttributes ?? []).some(
      pa => pa.attributeToken === publishedAttributeToken
        && pa.ownerType === PublishedAttributeOwnerType.SYSTEM,
    );
  // System PAs are managed by Alloy and cannot be edited from the workflow editor.
  const canEditPublishedAttribute = hasUpdatePublishedAttributesPermission
    && !isSystemPublishedAttribute;

  // Get the objectType from the published attribute
  const publishedAttribute = workflowProps?.publishedAttributes?.find(
    pa => pa.attributeToken === publishedAttributeToken,
  );
  const objectType = publishedAttribute?.objectType;

  useNodeFormAutoSave<FormState>({
    fn: handleAutoSave,
    // do not autosave published attribute data
    ignoreFields: isPublishedAttribute
      ? [...publishedAttributeFormFields, 'dataType']
      : ['dataType'],
  });

  const isDataTypeChangeConfirmationModalOpen = dataType && hasDependents
    && focusedAction.input.dataType !== dataType?.value;

  return (
    <StyledAttributeFormContainer>
      {isDataTypeChangeConfirmationModalOpen && (
        <ConfirmDataTypeChangeModal
          isOpen
          focusedAction={focusedAction}
          focusedActionDependents={focusedActionDependents}
          onSave={data => handleAutoSave(data)}
        />
      )}

      <StyledLeftColumnContainer>
        {isPublishedAttribute ? (
          <Controller
            control={control}
            name="attributeName"
            rules={{
              required: {
                value: true,
                message: validationMessages.attributeName.required,
              },
              validate: {
                uniqueName: (value: string) => {
                  const fieldState = getFieldState('attributeName', formState);
                  if (!fieldState.isDirty) return true;
                  const originalAttributeName = getPublishedDetails(focusedAction)?.attributeName
                    || null;
                  return (
                    isAttributeNameUnique(
                      workflowProps?.publishedAttributes || [],
                      value,
                      originalAttributeName,
                    )
                  || validationMessages.attributeName.uniqueName
                  );
                },
              },
            }}
            render={({
              field: {
                onChange, onBlur, value, ref,
              },
            }) => (
              <StyledTextField
                diffStatus={diffStatus}
              >
                <TextField
                  ref={ref}
                  error={formState.errors?.attributeName?.message}
                  label="Display Name"
                  type="text"
                  disabled={
                !inEditMode || (isPublishedAttribute && !canEditPublishedAttribute)
              }
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                />
              </StyledTextField>
            )}
          />
        ) : (
          <StyledTextField diffStatus={diffStatus}>
            <TextField
              label="Display Name"
              type="text"
              {...preventEmptyRegister('attributeName', {
                validate: value => (
                  existingInputNames.has(value)
                    ? 'An input attribute with this name already exists in this workflow version.'
                    : true
                ),
              })}
              disabled={!inEditMode}
              error={inEditMode && formState.errors?.attributeName?.message}
            />
          </StyledTextField>
        )}
        <Controller
          name="dataType"
          control={control}
          rules={{
            deps: isPublishedAttribute
              ? ['validValueMax', 'validValueMin']
              : [],
          }}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              isMulti={false}
              label="Data Type"
              isDisabled={!inEditMode || isPublishedAttribute}
              onChange={(newValue: Option) => {
                onChange(newValue);
                if (!hasDependents) {
                  handleSubmit(handleAutoSave)();
                }
              }}
              options={dataTypeOptions}
              selectedOptions={typeof value === 'string' ? { value, label: capitalize(value) } : value}
              customStyleFunctions={getDiffStyleFunction(diffStatus)}
            />
          )}
        />
        {isPublishedAttribute && (
          <>
            <Controller
              control={control}
              name="groupingToken"
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  label="Grouping"
                  isMulti={false}
                  isSearchable
                  onChange={(newValue: Option) => {
                    onChange(newValue.value);
                  }}
                  selectedOptions={groupingOptions.find(option => option.value === value) || null}
                  options={groupingOptions}
                  isDisabled={!inEditMode || attributeGroupingsLoading
                    || !canEditPublishedAttribute || objectType === 'transaction'}
                  customStyleFunctions={getDiffStyleFunction(diffStatus)}
                  placeholder={
                    attributeGroupingsLoading
                      ? 'Published Attribute groupings loading...'
                      : 'Select a grouping to add your published attribute to'
                  }
                />
              )}
            />
            <DefaultValueField
              dataType={dataType?.value}
              control={control}
              register={register}
              errors={inEditMode && formState.errors}
              setValue={setValue}
              disabled={!inEditMode || !canEditPublishedAttribute}
            />
            <TextField
              label="Default Value Note"
              type="text"
              {...register('defaultValueNote')}
              disabled={!inEditMode || !canEditPublishedAttribute}
            />
            {dataType?.value === 'string' && (
              <ValidValuesField
                control={control}
                errors={formState.errors}
                disabled={!inEditMode || !canEditPublishedAttribute}
              />
            )}
            <AcceptedValuesField
              control={control}
              dataType={dataType?.value}
              errors={formState.errors}
              disabled={!inEditMode || !canEditPublishedAttribute}
            />
          </>
        )}

      </StyledLeftColumnContainer>
      <StyledRightColumnContainer
        isPublishedAttribute={isPublishedAttribute}
      >
        {isPublishedAttribute && (
          <>
            <Box justifyContent="flex-end">
              {!isActionDataObjectEphemeral(focusedAction)
                && (
                <Token
                  style={{ marginBottom: 'calc(-1 * var(--cl-space-5))' }}
                  displayContent={publishedAttributeToken}
                  copyContent={publishedAttributeToken}
                />
                )}
            </Box>
            <Controller
              control={control}
              name="containsPii"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  checked={value}
                  disabled
                  label={PII_CHECKBOX_LABEL}
                  onChange={onChange}
                  helpText={PII_CHECKBOX_HELP_TEXT}
                />
              )}
            />
            <Controller
              control={control}
              name="hasExpiration"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  onChange={onChange}
                  checked={value}
                  label="This attribute expires"
                  disabled={!inEditMode || !canEditPublishedAttribute}
                />
              )}
            />

            {hasExpiration && (
              <TextField
                type="number"
                label="Days Until Expiration"
                required
                {...register('valueExpiresInDays', {
                  valueAsNumber: true,
                })}
                disabled={!inEditMode || !canEditPublishedAttribute}
              />
            )}
            <Controller
              control={control}
              name="agentCanCreateValue"
              render={({ field: { onChange, value } }) => (
                <Box justifyContent="flex-start">
                  <Checkbox
                    onChange={onChange}
                    checked={value}
                    label="Agents can update this attribute's values in the dashboard"
                    disabled={!inEditMode || !canEditPublishedAttribute}
                  />
                  <Tooltip
                    side="top"
                    content={getAgentUpdateValueTooltipText(objectType)}
                  >
                    <Icon icon={IconName.Info} size="small" />
                  </Tooltip>
                </Box>
              )}
            />
          </>
        )}
        <Controller
          data-dd-action-name="input-jq-checkbox"
          control={control}
          name="hasJqFilter"
          render={({ field: { onChange, value } }) => (
            <StyledCheckbox
              diffStatus={diffStatus}
            >
              <Checkbox
                checked={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const nextChecked = typeof e === 'boolean'
                    ? e
                    : (e?.target?.checked ?? e?.currentTarget?.checked);
                  onChange(e);

                  // we need to clear the jqFilter when the checkbox is unchecked, so that we don't save irrelevant jq function
                  // when the usingJq (this checkbox) is set to false.
                  if (nextChecked === false && jqFilter) {
                    setValue('jqFilter', '', { shouldDirty: true });
                  }
                }}
                label="Parse incoming values with JQ?"
                disabled={!inEditMode || (isPublishedAttribute && !canEditPublishedAttribute)}
              />
            </StyledCheckbox>
          )}
        />
        <StyledTextAreaWrapper diffStatus={diffStatus}>
          <TextArea
            data-dd-action-name="input-jq-filter-textarea"
            label="JQ Formula"
            {...register('jqFilter', {
              disabled: (
                !inEditMode || !usingJq
              || (isPublishedAttribute && !canEditPublishedAttribute)
              ),
            })}
            showLineNumbers
            rows={8}
          />
        </StyledTextAreaWrapper>
      </StyledRightColumnContainer>
    </StyledAttributeFormContainer>
  );
}

